import React, { useState } from "react"
import { css, cx } from "@emotion/css"
import { CheckListItem, CheckListItemProps } from "./CheckListItem"

import groupBy from "lodash/fp/groupBy"
import { splitArrayAt } from "utils"
import { useI18n } from "providers/I18n"
import TextField, { TextFieldProps } from "@material-ui/core/TextField"
import Close from "@material-ui/icons/Close"

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
  `,
  selected: css`
    padding-top: var(--spacing-m);
  `,
  fieldset: css`
    border: none;
    padding: var(--spacing-m) 0;
    margin: 0;
  `,
  selectedGroupTitle: css`
    line-height: 1rem;
    padding: 0 2px var(--spacing-xxs);
  `,
  moreOptions: css`
    margin-top: var(--spacing-xxs);
    cursor: pointer;
  `,
  searchField: css`
    margin-bottom: var(--spacing-xxs);
    width: 200px;
  `,
  items: css`
    margin-bottom: var(--spacing-xxl);
  `,
  other: css`
    margin-top: var(--spacing-xxs);
    margin-bottom: calc(var(--spacing-s) * -1);
  `,
  closeIcon: css`
    width: 18px !important;
    height: 18px !important;
    background-color: var(--dark-grey);
    color: var(--white);
    border-radius: 50%;
    cursor: pointer;
  `,
}

const MAX_OPTIONS_LENGTH = 10

export type CheckListProps = {
  options: CheckListItemProps[]
  name: string
  label: string
  totalChecked?: number
  className?: string
  fieldsetClassName?: string
  hasTranslation?: boolean
  translationPrefix?: string
  disableMaxOptions?: boolean
}

const groupChecked = groupBy<CheckListItemProps>((x) =>
  x.checked ? "selected" : "unselected",
)

export function CheckList(props: CheckListProps) {
  const { t } = useI18n()
  const {
    options,
    totalChecked,
    label,
    name,
    className,
    fieldsetClassName,
    hasTranslation,
    translationPrefix,
    disableMaxOptions,
  } = props
  const [search, setSearch] = useState<string>("")
  const [displayOptionsLength, setDisplayOptionsLength] = useState(
    disableMaxOptions ? Infinity : MAX_OPTIONS_LENGTH,
  )
  const grouped = groupChecked(options)
  const unselectedOptions = grouped["unselected"] || []
  const selectedOptions = grouped["selected"] || []
  const filteredOptions = Boolean(search)
    ? unselectedOptions.filter((opt) => {
        const v = (
          opt.filteringValue ||
          opt.searchLabel ||
          (typeof opt.label === "string" && opt.label) ||
          (hasTranslation &&
            t(`${translationPrefix}.${opt.value || opt.filteringValue}`)) ||
          opt.value ||
          ""
        ).toLowerCase()
        return v.includes(search.toLowerCase()) // filteringValue can be set to be more specific on the search
      })
    : unselectedOptions

  const [first, rest] = splitArrayAt(displayOptionsLength, filteredOptions)

  return (
    <div className={cx(styles.root, className)}>
      <legend className="BodyBody-2BlackLight">{label}</legend>
      {selectedOptions.length ? (
        <div className={styles.selected}>
          <div
            className={cx(
              "BodyBody-2Dark-greyRegular",
              styles.selectedGroupTitle,
            )}
          >
            {totalChecked ?? 0} selected
          </div>
          <div className={styles.items}>
            {selectedOptions.map((o, index) => (
              <CheckListItem key={o.value + index} name={name} {...o} />
            ))}
          </div>
          {first.length ? (
            <p className={cx("BodyBody-2Dark-greyRegular", styles.other)}>
              Other
            </p>
          ) : null}
        </div>
      ) : null}
      <fieldset className={cx(styles.fieldset, fieldsetClassName)}>
        {options.length > MAX_OPTIONS_LENGTH && !disableMaxOptions && (
          <Input
            value={search}
            placeholder={t("checklist.type-for-more")}
            onChange={(e) => setSearch(e.currentTarget.value)}
            className={styles.searchField}
            InputProps={{
              endAdornment: search && (
                <Close
                  onClick={() => setSearch("")}
                  className={styles.closeIcon}
                />
              ),
            }}
          />
        )}

        {!!first?.length ? (
          first.map((o, index) => (
            <CheckListItem
              key={o.value + index}
              name={name}
              resetSearch={setSearch}
              index={index}
              {...o}
            />
          ))
        ) : (
          <div className={"BodyBody-1Dark-greyLight-Center"}>
            {t("no.search.results")}
          </div>
        )}
        {rest.length > 0 && (
          <div
            onClick={() => setDisplayOptionsLength(filteredOptions.length)}
            className={cx("BodyBody-2Primary-mainBold", styles.moreOptions)}
          >{`Show ${rest.length} more`}</div>
        )}
      </fieldset>
    </div>
  )
}

const Input = (props: TextFieldProps) => (
  <TextField
    size="small"
    variant="outlined"
    color="primary"
    fullWidth
    {...props}
  />
)

//

export * from "./CheckListItem"
export * from "./CheckListItemLabel"
